import React, { memo, useEffect, useState } from 'react';
import { Stack, Image, Button, Col } from 'react-bootstrap';
import { S3_URL, USER_TYPES } from 'constants';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { useNavigate } from 'react-router-dom';
import {
	useAcceptConnectionRequestMutation,
	useAddToTalentMutation,
	useRemoveConnectRequestMutation,
	useSendConnectRequestMutation,
} from 'services';
import SaveTalentBtn from '../ActionButton/SaveTalent';
import { useQueryClient } from '@tanstack/react-query';
import useTranslate from 'hooks/useTranslate';
import useCommonStore, { setModalConfig } from 'store/common';
import useAuthStore, { updateUserInfo } from 'store/auth';
import ConnectBtn from '../ActionButton/ConnectBtn';
import './styles.scss';

const CandidateChips: React.FC<any> = ({ applicantList, indexKey }) => {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const [isFavourite, setIsFavourite] = useState<boolean>(applicantList?.isSaved);
	const { mutateAsync: addToFavMutation } = useAddToTalentMutation();
	const { userType = '', connection = 0 }: any = useAuthStore((state) => state?.userInfo?.user);
	const { hideCommonModal }: any = useCommonStore((state) => state);
	const { mutateAsync: sendRequestMutation } = useSendConnectRequestMutation();
	const { mutateAsync: removeConnectionMutation } = useRemoveConnectRequestMutation();
	const { mutateAsync: acceptRequestMutation } = useAcceptConnectionRequestMutation();

	const [isConnected, setIsConnected] = useState(applicantList?.isConnected);
	const [connectRequestSentBySelf, setConnectRequestSentBySelf] = useState(false);
	const [buttonText, setButtonText] = useState('');

	useEffect(() => {
		if (applicantList) {
			setIsConnected(applicantList?.isRequested ? 2 : applicantList?.isConnected ? 1 : 0);
			setConnectRequestSentBySelf(applicantList?.sendedBy || false);
		}
	}, [applicantList?.userId]);

	useEffect(() => {
		let btnText = '';
		if (isConnected === 2 && connectRequestSentBySelf) btnText = translate('Button.requested');
		else if (isConnected === 2) btnText = translate('Button.accept');
		else if (isConnected === 1) btnText = translate('Button.connected');
		else btnText = translate('Button.connect');
		setButtonText(btnText);
	}, [isConnected, connectRequestSentBySelf]);

	const addToFavorite = () => {
		setIsFavourite((prev) => !prev);
		addToFavMutation({ userId: applicantList.userId })
			.then((_res: any) => {
				queryClient.invalidateQueries({ queryKey: ['get-saved-talent'] });
			})
			.catch(() => {
				setIsFavourite((prev) => !prev);
			});
	};

	const handleConnectRequest = () => {
		if (isConnected === 0) {
			sendConnectionRequest();
		} else if (isConnected === 2 && connectRequestSentBySelf) {
			removeConnectionModal();
		} else if (isConnected === 2) {
			handleAcceptRequest();
		}
	};

	const sendConnectionRequest = () => {
		sendRequestMutation({ id: Number(applicantList?.userId) }).then((res: any) => {
			if (res?.success) {
				setIsConnected(2);
				setConnectRequestSentBySelf(true);
			}
		});
	};

	const removeConnectionModal = () => {
		setModalConfig({
			visible: true,
			type: 'confirmModal',
			onClick: removeConnection,
			data: {
				title: translate('Modal.wantToCancelRequest'),
			},
			props: {
				dialogClassName: 'confirm-modal',
			},
		});
	};

	const removeConnection = () => {
		removeConnectionMutation(applicantList?.userId).then((res: any) => {
			if (res?.success) {
				setIsConnected(0);
				setConnectRequestSentBySelf(false);
				setModalConfig({
					visible: false,
				});
			}
		});
	};

	const handleAcceptRequest = () => {
		acceptRequestMutation(Number(applicantList?.userId)).then((_res: any) => {
			if (_res?.success) {
				updateUserInfo({ connection: connection + 1 });
				setIsConnected(1);
				setConnectRequestSentBySelf(false);
			}
		});
	};

	const handleRejectRequest = () => {
		removeConnectionMutation(Number(applicantList?.userId)).then((_res: any) => {
			if (_res?.success) {
				setIsConnected(0);
				setConnectRequestSentBySelf(false);
			}
		});
	};

	return (
		<Col key={`${indexKey}`} xxl={4} xl={6} md={4} sm={6}>
			<Stack className="member-box d-flex">
				<div className="member-box-profile d-flex align-items-center gap-2">
					<div className="member-box-profile-img">
						<Image
							onClick={() => {
								navigate(`/seeker/profile/${applicantList?.userId}`);
							}}
							src={`${S3_URL}${applicantList.profilePicture}`}
							onError={({ currentTarget }) => {
								currentTarget.onerror = null;
								currentTarget.src = IMAGE_PATHS.UserDefault;
							}}
							className="w-100 h-100 rounded-circle"
							loading="lazy"
						/>
					</div>
					<div
						className="member-box-profile-info"
						onClick={() => {
							navigate(`/seeker/profile/${applicantList?.userId}`);
						}}
					>
						<h6 className="mb-1 fw-bold">{applicantList.name}</h6>
						<p className="mb-0">{applicantList?.address}</p>
					</div>
				</div>
				<div className="member-box-content">
					<p className="mb-0">
						{[applicantList.designation, applicantList?.companyName].filter(Boolean).join(', ') ??
							''}
					</p>
				</div>
				{userType === USER_TYPES.COMPANY ? (
					<SaveTalentBtn
						isFavourite={isFavourite}
						handleClick={(e: any) => {
							e.stopPropagation();
							addToFavorite();
						}}
						className="filled-btn fav-btn rounded-pill lh-1"
					/>
				) : (
					<div className="d-flex gap-2 mt-auto">
						<Button
							onClick={() => handleConnectRequest()}
							className={`filled-btn rounded-pill lh-1 ${isConnected === 2 && connectRequestSentBySelf ? 'request-sent' : isConnected === 1 ? 'connected' : ''}`}
						>
							{buttonText}
						</Button>
						{isConnected === 2 && !connectRequestSentBySelf && (
							<div>
								<Button
									onClick={() => handleRejectRequest()}
									className="filled-btn rounded-pill lh-1"
								>
									{translate('Button.reject')}
								</Button>
							</div>
						)}
					</div>
				)}
			</Stack>
		</Col>
	);
};
export default memo(CandidateChips);
